import React from "react";

export type StarSizeType = "S" | "M" | "L";

type Props = {
  score: number;
  size?: StarSizeType;
  onClick?: (score: number) => void;
};

export default function StarScore({ score, onClick, size = "S" }: Props) {
  return (
    <div className="flex items-center">
      {Array.from({ length: 5 }, (_, index) => index).map((value, index) => {
        let starComponent;
        if (value + 1 <= Math.round(score)) {
          starComponent = <ActiveStarScore key={value} size={size} />;
        } else {
          starComponent = <InactiveStarScore key={value} size={size} />;
        }
        return (
          <div key={index} onClick={() => onClick?.(index + 1)}>
            {starComponent}
          </div>
        );
      })}
    </div>
  );
}

export type StarScoreIconProps = {
  size?: StarSizeType;
};

export const ActiveStarScore = ({ size }: StarScoreIconProps) => {
  switch (size) {
    case "S":
      return <ActiveLittleStarScore />;
    case "M":
      return <ActiveMiddleStarScore />;
    case "L":
      return <ActiveLargeStarScore />;
    default:
      return <ActiveLittleStarScore />;
  }
};

export const InactiveStarScore = ({ size }: StarScoreIconProps) => {
  switch (size) {
    case "S":
      return <InactiveLittleStarScore />;
    case "M":
      return <InactiveMiddleStarScore />;
    case "L":
      return <InactiveLargeStarScore />;
    default:
      return <InactiveLittleStarScore />;
  }
};

export const ActiveLargeStarScore = () => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M30 4.87978L37.2663 19.6029L37.3827 19.8387L37.6428 19.8765L53.8908 22.2374L42.1336 33.6978L41.9454 33.8813L41.9898 34.1404L44.7653 50.3227L30.2327 42.6824L30 42.5601L29.7673 42.6824L15.2347 50.3227L18.0102 34.1404L18.0546 33.8813L17.8664 33.6978L6.10925 22.2374L22.3572 19.8765L22.6174 19.8387L22.7337 19.6029L30 4.87978Z"
      fill="url(#paint0_linear_1741_12244)"
      stroke="#EA27C2"
    />
    <defs>
      <linearGradient
        id="paint0_linear_1741_12244"
        x1="56.25"
        y1="3.75"
        x2="0.355258"
        y2="18.1358"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E0004D" />
        <stop offset="1" stopColor="#EA27C2" />
      </linearGradient>
    </defs>
  </svg>
);

export const InactiveLargeStarScore = () => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M30 6.00955L36.8179 19.8242L37.0506 20.2957L37.5709 20.3713L52.8163 22.5865L41.7846 33.3398L41.4081 33.7067L41.497 34.2249L44.1012 49.4087L30.4653 42.2399L30 41.9952L29.5347 42.2399L15.8988 49.4087L18.503 34.2249L18.5919 33.7067L18.2154 33.3398L7.18373 22.5865L22.4291 20.3713L22.9494 20.2957L23.1821 19.8242L30 6.00955Z"
      fill="#F3F3F3"
      stroke="#E7E7E8"
      strokeWidth="2"
    />
  </svg>
);

export const ActiveMiddleStarScore = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 1.5L15.0859 7.75266L21.9861 8.75532L16.993 13.6223L18.1717 20.4947L12 17.25L5.82825 20.4947L7.00695 13.6223L2.01391 8.75532L8.91413 7.75266L12 1.5Z"
      fill="#EA27C2"
    />
  </svg>
);

export const InactiveMiddleStarScore = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 1.5L15.0859 7.75266L21.9861 8.75532L16.993 13.6223L18.1717 20.4947L12 17.25L5.82825 20.4947L7.00695 13.6223L2.01391 8.75532L8.91413 7.75266L12 1.5Z"
      fill="#E7E7E8"
    />
  </svg>
);

export const ActiveLittleStarScore = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1L10.0572 5.16844L14.6574 5.83688L11.3287 9.08156L12.1145 13.6631L8 11.5L3.8855 13.6631L4.6713 9.08156L1.3426 5.83688L5.94275 5.16844L8 1Z"
      fill="#EA27C2"
    />
  </svg>
);

export const InactiveLittleStarScore = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 1L10.0572 5.16844L14.6574 5.83688L11.3287 9.08156L12.1145 13.6631L8 11.5L3.8855 13.6631L4.6713 9.08156L1.3426 5.83688L5.94275 5.16844L8 1Z"
      fill="#E7E7E8"
    />
  </svg>
);
